// Pure state machine for one side of a compare/compat pair, picked through
// scan v2 (spec §4.2):
//
// empty → identifying → candidates ──confirm──→ ready(product)
//                 │          └─reject all──→ label
//                 ├─ next=result ──────────→ ready(product, verified, canNotMine)
//                 └─ next=capture_label ───→ label
// label: parts ≤ 3 → reading → ready | retry(attempts_left) | fallback
// fallback: paste text → ready | send to team → awaitingTeam
// ready(verified) ──"Not my product?"──→ candidates | label
// any → error(retry) ; any → empty (clear)
import type {
  LabelMeta,
  ScanCandidate,
  ScanConfirmResult,
  ScanIdentifyResult,
  ScanLabelResult,
  ScanLabelSource,
  ScanRejectResult,
} from '../../../types/scanV2';

/** Label photos the server reads per attempt. */
export const MAX_LABEL_PARTS = 3;
/** Label attempts before the fallback (paste or team). */
export const MAX_LABEL_ATTEMPTS = 3;

/** A product that fills a side. */
export type SideProduct = {
  productId: number;
  name: string;
  brand?: string;
  imageUrl?: string;
  /** Verified catalog ingredients. */
  verified: boolean;
  scanId?: string;
  ingredientsStatus?: string;
  /** How a label product's ingredients were read (label step only). */
  labelSource?: ScanLabelSource;
};

/** Brand and name of a confirmed candidate. */
export type FoundProduct = { name: string; brand?: string };

export type BrandNamePending = 'label' | 'paste' | 'team';

type LabelState = {
  step: 'label';
  scanId: string;
  parts: Blob[];
  attemptsLeft: number;
  hint?: string;
  /** Identify/confirm reason that sent the user here (none_found, …). */
  reason?: string;
  /** Confirmed product whose ingredient list is not online yet. */
  found?: FoundProduct;
  reading: boolean;
  error?: string;
  /** The ready product whose unconfirmed (AI, no source) list this label confirms. */
  verifying?: SideProduct;
};

type FallbackState = { step: 'fallback'; scanId: string; busy: boolean; error?: string; verifying?: SideProduct };

/** ingredients_status of a list the AI gave without a source (owner-only, moderated). */
export const UNVERIFIED_STATUS = 'unverified';

export type SideState =
  /** `barcode` refills the entry (e.g. after a failed identify). */
  | { step: 'empty'; barcode?: string }
  | { step: 'identifying'; scanId?: string; barcode?: string; back: SideState }
  | { step: 'candidates'; scanId: string; candidates: ScanCandidate[] }
  | { step: 'confirming'; scanId: string; candidateId: string; candidates: ScanCandidate[] }
  | { step: 'ready'; product: SideProduct; canNotMine: boolean }
  | LabelState
  | FallbackState
  | {
      step: 'needsBrandName';
      scanId: string;
      pending: BrandNamePending;
      /** Pasted text waiting for brand and name. */
      text?: string;
      back: LabelState | FallbackState;
    }
  | { step: 'awaitingTeam'; scanId: string; productId?: number }
  /** `retryIdentify`: "Try again" runs the failed identify again instead of going back. */
  | { step: 'error'; message: string; retry: SideState; retryIdentify?: boolean };

export type SideAction =
  | { type: 'IDENTIFY_START'; barcode?: string }
  | { type: 'IDENTIFIED'; result: ScanIdentifyResult }
  | { type: 'CONFIRM_START'; candidateId: string }
  | { type: 'CONFIRMED'; result: ScanConfirmResult }
  | { type: 'REJECT_START' }
  | { type: 'NOT_MINE_START' }
  | { type: 'REJECTED'; result: ScanRejectResult }
  | { type: 'ADD_PART'; part: Blob }
  | { type: 'REMOVE_PART'; index: number }
  | { type: 'LABEL_START' }
  | { type: 'LABEL_RESULT'; result: ScanLabelResult; meta?: LabelMeta }
  | { type: 'NEED_BRAND_NAME'; pending: BrandNamePending; text?: string }
  | { type: 'CANCEL' }
  | { type: 'PICK_FAVORITE'; product: SideProduct }
  | { type: 'FAIL'; message: string; status?: number }
  | { type: 'RETRY' }
  | { type: 'CLEAR' }
  /** Photograph the label to confirm the ready product's unverified list (same scan, free). */
  | { type: 'VERIFY_START' }
  /** Leave that label step: back to the unverified product. */
  | { type: 'VERIFY_CANCEL' };

const REASON_MESSAGES: Record<string, string> = {
  not_cosmetic: 'This does not look like a cosmetic product.',
  out_of_scope: 'We check skin and body care only (not hair, nails, fragrance or oral care).',
  provider_unavailable: 'Product recognition is busy right now. Try again in a moment.',
  session_closed: 'This scan has expired. Start again.',
  already_confirmed: 'Another product was already picked for this scan.',
  unknown_candidate: 'That product is no longer on the list. Pick another one.',
  not_a_verified_hit: 'This product cannot be changed here. Clear it and scan again.',
  ingredients_invalid: 'We could not read that list. Check it and try again.',
  fallback_not_available: 'Send three label photos before asking our team.',
  brand_name_required: 'Add the brand and the product name.',
};
const GENERIC_MESSAGE = 'Something went wrong. Try again.';

/** User-facing text for a known reason code, else undefined. */
export function knownReasonMessage(reason: unknown): string | undefined {
  return typeof reason === 'string' ? REASON_MESSAGES[reason] : undefined;
}

/** User-facing text for a scan v2 reason code. */
export function reasonMessage(reason: string | null | undefined): string {
  return (reason ? REASON_MESSAGES[reason] : undefined) ?? GENERIC_MESSAGE;
}

/** Trimmed text, or undefined when blank. */
function text(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed;
}

export function initialSideState(initial?: SideProduct): SideState {
  return initial ? { step: 'ready', product: initial, canNotMine: false } : { step: 'empty' };
}

function freshLabel(scanId: string, reason?: string | null, found?: FoundProduct): LabelState {
  return {
    step: 'label',
    scanId,
    parts: [],
    attemptsLeft: MAX_LABEL_ATTEMPTS,
    reading: false,
    ...(reason ? { reason } : {}),
    ...(found ? { found } : {}),
  };
}

/** Brand and name of a candidate, or undefined without a name. */
function foundProduct(c: ScanCandidate | undefined): FoundProduct | undefined {
  const name = text(c?.name);
  if (!name) return undefined;
  const brand = text(c?.brand);
  return brand ? { name, brand } : { name };
}

function fromCandidate(c: ScanCandidate | undefined, productId: number, scanId: string): SideProduct {
  return {
    productId,
    name: text(c?.name) ?? 'Your product',
    brand: text(c?.brand),
    imageUrl: text(c?.image_url),
    verified: false,
    scanId,
  };
}

/** A failed identify: back to where it started, keeping a typed barcode. */
function identifyFailed(state: Extract<SideState, { step: 'identifying' }>, message: string): SideState {
  if (state.back.step !== 'empty') return { step: 'error', message, retry: state.back };
  const retry: SideState = state.barcode ? { step: 'empty', barcode: state.barcode } : state.back;
  return { step: 'error', message, retry, retryIdentify: true };
}

function onIdentified(state: SideState, result: ScanIdentifyResult): SideState {
  if (state.step !== 'identifying') return state;
  const scanId = result.scan_id ?? state.scanId ?? '';
  const failed = identifyFailed(state, reasonMessage(result.reason));
  switch (result.next) {
    case 'result': {
      const first = result.candidates.length > 0 ? result.candidates[0] : undefined;
      const hit =
        result.candidates.find((c) => result.product_id !== undefined && c.product_id === result.product_id) ??
        first;
      const productId = result.product_id ?? hit?.product_id;
      if (!productId) return failed;
      return { step: 'ready', canNotMine: true, product: { ...fromCandidate(hit, productId, scanId), verified: true } };
    }
    case 'confirm':
      return { step: 'candidates', scanId, candidates: result.candidates };
    case 'capture_label':
      return freshLabel(scanId, result.reason);
    default:
      return failed;
  }
}

function onConfirmed(state: SideState, result: ScanConfirmResult): SideState {
  if (state.step !== 'confirming') return state;
  const candidates: SideState = { step: 'candidates', scanId: state.scanId, candidates: state.candidates };
  const chosen = state.candidates.find((c) => c.candidate_id === state.candidateId);
  if (result.next === 'result' && result.product_id) {
    const product = fromCandidate(chosen, result.product_id, state.scanId);
    return {
      step: 'ready',
      canNotMine: false,
      product: {
        ...product,
        verified: result.ingredients_status === 'verified',
        ...(result.ingredients_status ? { ingredientsStatus: result.ingredients_status } : {}),
      },
    };
  }
  if (result.next === 'capture_label') return freshLabel(state.scanId, result.reason, foundProduct(chosen));
  const retry = result.reason === 'session_closed' ? { step: 'empty' as const } : candidates;
  return { step: 'error', message: reasonMessage(result.reason), retry };
}

function onRejected(state: SideState, result: ScanRejectResult): SideState {
  if (state.step !== 'identifying' || state.back.step === 'empty') return state;
  const scanId = state.scanId ?? '';
  if (result.next === 'confirm') return { step: 'candidates', scanId, candidates: result.candidates };
  if (result.next === 'capture_label') return freshLabel(scanId);
  return { step: 'error', message: reasonMessage(result.reason), retry: state.back };
}

function onLabelStart(state: SideState): SideState {
  if (state.step === 'label') {
    if (state.reading || state.parts.length === 0) return state;
    const { error: _error, ...rest } = state;
    return { ...rest, reading: true };
  }
  if (state.step === 'fallback') {
    const { error: _error, ...rest } = state;
    return { ...rest, busy: true };
  }
  if (state.step === 'needsBrandName') {
    return state.back.step === 'label' ? { ...state.back, reading: true } : { ...state.back, busy: true };
  }
  return state;
}

function idle(state: LabelState | FallbackState): LabelState | FallbackState {
  return state.step === 'label' ? { ...state, reading: false } : { ...state, busy: false };
}

function onLabelResult(state: SideState, result: ScanLabelResult, meta?: LabelMeta): SideState {
  if (state.step !== 'label' && state.step !== 'fallback') return state;
  const scanId = result.scan_id ?? state.scanId;
  switch (result.next) {
    case 'result':
      if (!result.product_id) break;
      if (state.verifying) {
        const { ingredientsStatus: _old, labelSource: _oldSource, ...product } = state.verifying;
        return {
          step: 'ready',
          canNotMine: false,
          product: {
            ...product,
            productId: result.product_id,
            verified: false,
            scanId,
            ingredientsStatus: result.ingredients_status ?? 'pending_review',
            ...(result.source ? { labelSource: result.source } : {}),
          },
        };
      }
      return {
        step: 'ready',
        canNotMine: false,
        product: {
          productId: result.product_id,
          name: text(meta?.name) ?? 'Your product',
          brand: text(meta?.brand),
          verified: false,
          scanId,
          ...(result.ingredients_status ? { ingredientsStatus: result.ingredients_status } : {}),
          ...(result.source ? { labelSource: result.source } : {}),
        },
      };
    case 'capture_label': {
      const prevLeft = state.step === 'label' ? state.attemptsLeft : 1;
      const attemptsLeft = result.attempts_left ?? Math.max(0, prevLeft - 1);
      const verifying = state.verifying ? { verifying: state.verifying } : {};
      if (attemptsLeft <= 0) return { step: 'fallback', scanId, busy: false, ...verifying };
      return {
        step: 'label',
        scanId,
        parts: [],
        attemptsLeft,
        reading: false,
        ...(result.hint ? { hint: result.hint } : {}),
        ...verifying,
      };
    }
    case 'fallback':
      return { step: 'fallback', scanId, busy: false, ...(state.verifying ? { verifying: state.verifying } : {}) };
    case 'awaiting_team':
      return { step: 'awaitingTeam', scanId, ...(result.product_id ? { productId: result.product_id } : {}) };
    default:
      if (result.reason === 'session_closed') {
        return { step: 'error', message: reasonMessage(result.reason), retry: { step: 'empty' } };
      }
  }
  return { ...idle(state), scanId, error: reasonMessage(result.reason) };
}

/**
 * A 402 already opened the top-up sheet (apiFetch), so the side shows no
 * second message: it steps back to where the user was, input kept.
 */
function onOutOfCredits(state: SideState): SideState {
  switch (state.step) {
    case 'label': {
      const { error: _error, ...rest } = state;
      return { ...rest, reading: false };
    }
    case 'fallback': {
      const { error: _error, ...rest } = state;
      return { ...rest, busy: false };
    }
    case 'identifying':
      return state.back.step === 'empty' && state.barcode ? { step: 'empty', barcode: state.barcode } : state.back;
    case 'confirming':
      return { step: 'candidates', scanId: state.scanId, candidates: state.candidates };
    default:
      return state;
  }
}

function onFail(state: SideState, message: string, status?: number): SideState {
  if (status === 402) return onOutOfCredits(state);
  switch (state.step) {
    case 'label':
    case 'fallback':
      // The label parts stay on the side so the user can retry.
      return { ...idle(state), error: message };
    case 'identifying':
      return identifyFailed(state, message);
    case 'confirming':
      return {
        step: 'error',
        message,
        retry: { step: 'candidates', scanId: state.scanId, candidates: state.candidates },
      };
    default:
      return { step: 'error', message, retry: { step: 'empty' } };
  }
}

function onParts(state: SideState, action: Extract<SideAction, { type: 'ADD_PART' | 'REMOVE_PART' }>): SideState {
  if (state.step !== 'label' || state.reading) return state;
  const { error: _error, ...rest } = state;
  if (action.type === 'ADD_PART') {
    if (state.parts.length >= MAX_LABEL_PARTS) return state;
    return { ...rest, parts: [...state.parts, action.part] };
  }
  return { ...rest, parts: state.parts.filter((_, i) => i !== action.index) };
}

export function sideReducer(state: SideState, action: SideAction): SideState {
  switch (action.type) {
    case 'IDENTIFY_START':
      return { step: 'identifying', ...(action.barcode ? { barcode: action.barcode } : {}), back: state };
    case 'IDENTIFIED':
      return onIdentified(state, action.result);
    case 'CONFIRM_START':
      if (state.step !== 'candidates') return state;
      return { step: 'confirming', scanId: state.scanId, candidateId: action.candidateId, candidates: state.candidates };
    case 'CONFIRMED':
      return onConfirmed(state, action.result);
    case 'REJECT_START':
      if (state.step !== 'candidates') return state;
      return { step: 'identifying', scanId: state.scanId, back: state };
    case 'NOT_MINE_START':
      if (state.step !== 'ready' || !state.canNotMine) return state;
      return { step: 'identifying', scanId: state.product.scanId, back: state };
    case 'REJECTED':
      return onRejected(state, action.result);
    case 'ADD_PART':
    case 'REMOVE_PART':
      return onParts(state, action);
    case 'LABEL_START':
      return onLabelStart(state);
    case 'LABEL_RESULT':
      return onLabelResult(state, action.result, action.meta);
    case 'NEED_BRAND_NAME':
      if (state.step !== 'label' && state.step !== 'fallback') return state;
      return {
        step: 'needsBrandName',
        scanId: state.scanId,
        pending: action.pending,
        ...(action.text === undefined ? {} : { text: action.text }),
        back: idle(state),
      };
    case 'CANCEL':
      return state.step === 'needsBrandName' ? state.back : state;
    case 'PICK_FAVORITE':
      return { step: 'ready', product: action.product, canNotMine: false };
    case 'FAIL':
      return onFail(state, action.message, action.status);
    case 'RETRY':
      return state.step === 'error' ? state.retry : state;
    case 'CLEAR':
      return { step: 'empty' };
    case 'VERIFY_START':
      if (state.step !== 'ready' || state.product.ingredientsStatus !== UNVERIFIED_STATUS || !state.product.scanId) return state;
      return { ...freshLabel(state.product.scanId), verifying: state.product };
    case 'VERIFY_CANCEL':
      if ((state.step !== 'label' && state.step !== 'fallback') || !state.verifying) return state;
      return { step: 'ready', product: state.verifying, canNotMine: false };
  }
}
